import {
    NestedSelectableOptions,
    SelectableOptions,
} from 'commons/components/searchable-dropdown/searchable-dropdown.type';
import type { PocInputValidity } from 'commons/types';
import { ErrorMapping } from 'core/types/error-mapping';
import { OperatorCustomNotificationFormValidationMessages } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.validation';
import { FormSection } from 'modules/settings/components/custom-notifications/custom-notification-form.style';
import { CustomNotificationMonitoringPeriodSection } from 'modules/settings/components/custom-notifications/custom-notification-monitoring-period-section';
import { CustomNotificationMultiSelectSection } from 'modules/settings/components/custom-notifications/custom-notification-multi-select-section';
import { CustomNotificationNameSection } from 'modules/settings/components/custom-notifications/custom-notification-name-section';
import {
    OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX,
    OPERATOR_CUSTOM_NOTIFICATION_FORM_TRIGGERS_HINT_KEY,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.constants';
import { DraftSelectionFormState } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.model';

export interface OperatorCustomNotificationFormFieldsProps {
    name: string;
    deviceTypes: DraftSelectionFormState;
    triggers: DraftSelectionFormState;
    windowDays: string;
    deviceTypeOptions: SelectableOptions[];
    triggerOptions: NestedSelectableOptions[];
    isLoadingDeviceTypes?: boolean;
    isLoadingTriggers?: boolean;
    validationMessages: OperatorCustomNotificationFormValidationMessages;
    nameValidity?: PocInputValidity;
    nameCustomValidationCode?: ErrorMapping['type'];
    triggersValidity?: PocInputValidity;
    windowDaysValidity?: PocInputValidity;
    onNameChange: (value: string) => void;
    onDeviceTypesDraftChange: (value: string[]) => void;
    onDeviceTypesApply: (value: string[]) => void;
    onTriggersDraftChange: (value: string[]) => void;
    onTriggersApply: (value: string[]) => void;
    onWindowDaysChange: (value: string) => void;
}

export const OperatorCustomNotificationFormFields: React.FC<OperatorCustomNotificationFormFieldsProps> = ({
    name,
    deviceTypes,
    triggers,
    windowDays,
    deviceTypeOptions,
    triggerOptions,
    isLoadingDeviceTypes,
    isLoadingTriggers,
    validationMessages,
    nameValidity,
    nameCustomValidationCode,
    triggersValidity,
    windowDaysValidity,
    onNameChange,
    onDeviceTypesDraftChange,
    onDeviceTypesApply,
    onTriggersDraftChange,
    onTriggersApply,
    onWindowDaysChange,
}) => (
    <FormSection>
        <CustomNotificationNameSection
            name={name}
            labelKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.name.label`}
            placeholderKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.name.placeholder`}
            inputName="name"
            autofocus
            validity={nameValidity}
            validationMessages={validationMessages.name}
            customValidationCode={nameCustomValidationCode}
            onChange={onNameChange}
        />

        <CustomNotificationMultiSelectSection
            name="deviceTypes"
            titleKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.device-types.label`}
            placeholderKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.device-types.placeholder`}
            options={deviceTypeOptions}
            draftIds={deviceTypes.draftIds}
            selectedIds={deviceTypes.selectedIds}
            isLoading={isLoadingDeviceTypes}
            searchable
            onDraftChange={onDeviceTypesDraftChange}
            onApplySelection={onDeviceTypesApply}
            onClear={() => onDeviceTypesApply([])}
        />

        <CustomNotificationMultiSelectSection
            name="triggers"
            titleKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.triggers.label`}
            placeholderKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.triggers.placeholder`}
            hintKey={OPERATOR_CUSTOM_NOTIFICATION_FORM_TRIGGERS_HINT_KEY}
            options={triggerOptions}
            draftIds={triggers.draftIds}
            selectedIds={triggers.selectedIds}
            isLoading={isLoadingTriggers}
            searchable
            expandGroupsByDefault={triggerOptions.length === 1}
            disableEmptyGroups
            validity={triggersValidity}
            validationMessages={validationMessages.triggers}
            onDraftChange={onTriggersDraftChange}
            onApplySelection={onTriggersApply}
            onClear={() => onTriggersApply([])}
        />

        <CustomNotificationMonitoringPeriodSection
            value={windowDays}
            labelKey={`${OPERATOR_CUSTOM_NOTIFICATION_FORM_I18N_PREFIX}.monitoring-period.label`}
            validity={windowDaysValidity}
            onChange={onWindowDaysChange}
        />
    </FormSection>
);
